import { cloneElement, ReactElement } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import AppBar from '@mui/material/AppBar';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';

// project imports
import MenuList from './MenuList';
import useConfig from 'hooks/useConfig';
import { appBarHeight, HBAR_HEIGHT_VAR, HBAR_MIN_HEIGHT, useMeasuredHeightVar } from './shellOffsets';

// ==============================|| HORIZONTAL MENU LIST ||============================== //

interface ElevationScrollProps {
  children: ReactElement<{ elevation?: number }>;
  window?: Window | Node;
}

function ElevationScroll({ children, window }: ElevationScrollProps) {
  const theme = useTheme();

  /**
   * Note that you normally won't need to set the window ref as useScrollTrigger will default to window.
   * This is only being set here because the demo is in an iframe.
   */
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
    target: window!
  });

  theme.shadows[4] = theme.vars.customShadows.z1;

  return cloneElement(children, {
    elevation: trigger ? 4 : 0
  });
}

// ==============================|| HORIZONTAL MENU LIST ||============================== //

export default function HorizontalBar() {
  const {
    state: { container }
  } = useConfig();

  // ⚠ This bar publishes its own height for the same reason the AppBar does: a nav label that wraps to two
  // lines used to overflow a fixed 62px box and, being `position: fixed` above the content, paint over the
  // page instead of pushing it down. It is now min-height, and the content offset reads the real number.
  const barRef = useMeasuredHeightVar(HBAR_HEIGHT_VAR);

  return (
    <ElevationScroll>
      <AppBar
        ref={barRef}
        sx={(theme) => ({
          // ⚠ MEASURED, not 71. This bar has to sit directly under whatever the AppBar actually measures; a
          // written number leaves the header covering the menu the moment the header grows.
          top: appBarHeight,
          bgcolor: 'background.paper',
          width: '100%',
          minHeight: HBAR_MIN_HEIGHT,
          justifyContent: 'center',
          borderTop: '1px solid',
          borderColor: 'grey.300',
          zIndex: 1098,
          ...theme.applyStyles('dark', {
            bgcolor: 'background.default',
            borderColor: 'background.paper'
          })
        })}
      >
        <Container maxWidth={container ? 'lg' : false}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <MenuList />
          </Box>
        </Container>
      </AppBar>
    </ElevationScroll>
  );
}
